<template>
  <main id="main-content" tabindex="-1">
    <div v-if="hasNoLicenses">You have no registered licenses</div>
    <section
      id="projects"
      :class="[{ hide_list: selectedComponent == null }]"
      :aria-hidden="modalOpen"
      :inert="modalOpen"
      v-if="!hasNoLicenses"
    >
      <!--------------------------------------------
      |
      | SEARCH BAR
      |
      --------------------------------------------->
      <div class="projects__search-bar center-align-parent">
        <SearchBar
          name="searchBar__projects"
          class="center-align-child"
          placeholder="Filter by project name"
          @search="search"
        />
      </div>

      <!--------------------------------------------
      |
      | HEADER
      |
      --------------------------------------------->
      <div class="section__top-bar">
        <h1 class="section__top-bar-title">
          <VisibleText>Projects </VisibleText>( {{ totalProjects }} )
        </h1>
        <div class="section__top-bar-actions-wrapper">
          <div style="display: flex; flex-direction: row">
            <ButtonIconOnly
              :icon="projectSortOrder == 'des' ? 'descending' : 'ascending'"
              style="width: 2rem; height: 2rem; border-radius: 5px; color: black"
              class="projects__view-select-btn"
              @click-handler="changeSortOrder"
            >
              <div v-if="projectSortOrder == 'des'">
                <VisibleText>newest first</VisibleText>
              </div>
              <div v-else>
                <VisibleText>oldest first</VisibleText>
              </div>
            </ButtonIconOnly>
            <form @submit.prevent="saveUIPreferences">
              <ButtonIconOnly
                icon="grid"
                id="grid-view"
                :class="[
                  {
                    'projects__view-select-active': selectedComponent == 'ProjectsGrid',
                  },
                  'projects__view-select-btn',
                ]"
                style="width: 2rem; height: 2rem"
                @click-handler="saveUIPreferences('ProjectsGrid')"
              >
                <VisibleText>grid view</VisibleText>
              </ButtonIconOnly>
              <ButtonIconOnly
                icon="list"
                id="list-view"
                :class="[
                  {
                    'projects__view-select-active': selectedComponent == 'ProjectsList',
                  },
                  'projects__view-select-btn',
                ]"
                style="width: 2rem; height: 2rem"
                @click-handler="saveUIPreferences('ProjectsList')"
              >
                <VisibleText>list view</VisibleText>
              </ButtonIconOnly>
            </form>
          </div>
        </div>
      </div>

      <div :class="createItemsWrapperClass" style="z-index: 0">
        <div
          id="create-content"
          class="create-content"
          :style="
            selectedComponent == 'ProjectsGrid' ? 'margin-left: 6rem; position: absolute;' : ''
          "
        >
          <button class="create-btn" type="button" @click="isProjectModalVisible = true">
            <span class="center-align-parent" id="create-icon-wrapper">
              <SvgIconDecorative icon="add" class="center-align-child" id="create-icon" />
            </span>
            <span class="create-title">
              <VisibleText>Create new project</VisibleText>
            </span>
            <span class="create-text">
              <VisibleText> Analyze data and create reports in just a few clicks </VisibleText>
            </span>
          </button>
        </div>
      </div>

      <!--------------------------------------------
      |
      | PROJECTS LSIT
      |
      --------------------------------------------->
      <Spinner
        :is-loading="isLoading"
        :message="'loading file, please wait'"
        :componentStyle="true"
        style="position: relative"
      />
      <PastProjects
        :class="itemsWrapperClass"
        :models="[BenchmarkingProject]"
        :search="query.term"
        :sortOrder="projectSortOrder"
        :selectedComponent="selectedComponent"
        @pastProjectsRetreived="pastProjectsRetreived($event)"
        :foundDatasets="datasets"
      >
      </PastProjects>
    </section>

    <!--------------------------------------------
    |
    | CREATE PROJECT MODAL
    |
    --------------------------------------------->
    <ProjectsCreateModal
      :show="isProjectModalVisible"
      @close="isProjectModalVisible = false"
      @foundDatasets="foundDatasets"
    />
  </main>
</template>

<script>
// Components
import { PastProjects } from '@/pigeonline/wrapper';
import ButtonIconOnly from '@/components/UI/Button/ButtonIconOnly.vue';
import SearchBar from '@/components/UI/SearchBar.vue';
import ProjectsCreateModal from '@/components/Projects/ProjectsCreateModal/ProjectsCreateModal.vue';
import SvgIconDecorative from '@/components/UI/Svg/SvgIconDecorative.vue';
import Spinner from '@/components/UI/Spinner.vue';

// Utils
import UserMixin from '@/utils/mixins/userMixin';

// Models
import BenchmarkingProject from '@/models/BenchmarkingProjectModel.js';
//import BenchmarkingDataset from "@/models/BenchmarkingDatasetModel.js"

export default {
  name: 'Projects',
  mixins: [UserMixin],
  components: {
    ButtonIconOnly,
    PastProjects,
    ProjectsCreateModal,
    SearchBar,
    SvgIconDecorative,
    Spinner,
  },
  data() {
    return {
      BenchmarkingProject: BenchmarkingProject,
      selectedComponent: null,
      totalProjects: 0,
      firstLoad: true,
      isLoading: false,
      isProjectModalVisible: false,
      datasets: [],
      query: {
        fields: ['name'],
        term: '',
      },
      projectSortOrder: 'des',
      vModels: {
        uiPreferences: {
          projects: {
            projectsViewMode: '',
          },
        },
      },
    };
  },
  created() {
    document.title = `Projects | ${this.$theme.theme.pageTitle}`;
    this.isLoading = true;
  },
  mounted() {
    this.$watch(
      'profile',
      function (newVal) {
        if (!newVal) return;
        this.setValue('projectsViewMode', newVal.projectsViewMode);
        this.checkUIPreferences();
      },
      { deep: true, immediate: true },
    );
  },
  computed: {
    modalOpen() {
      return this.$store.getters['getModalOpen'];
    },
    createItemsWrapperClass() {
      return [
        { 'projects-grid-wrapper': this.selectedComponent === 'ProjectsGrid' },
        { 'projects-list-wrapper': this.selectedComponent === 'ProjectsList' },
        'create-wrapper',
      ];
    },
    itemsWrapperClass() {
      return [
        { 'projects-grid-wrapper': this.selectedComponent === 'ProjectsGrid' },
        { 'projects-list-wrapper': this.selectedComponent === 'ProjectsList' },
        'projects__items-wrapper',
      ];
    },
  },
  methods: {
    checkUIPreferences() {
      if (this.firstLoad) {
        this.selectedComponent = this.vModels.uiPreferences.projects.projectsViewMode;
        this.firstLoad = false;
        if (!this.selectedComponent) {
          this.selectedComponent = 'ProjectsList';
        }
      }
    },
    changeSortOrder() {
      if (this.projectSortOrder == 'des') {
        this.projectSortOrder = 'asc';
      } else {
        this.projectSortOrder = 'des';
      }
    },
    foundDatasets(datasets) {
      this.datasets = datasets;
    },
    pastProjectsRetreived(projects) {
      this.totalProjects = projects.length;
      this.isLoading = false;
    },
    projectCreated(project) {
      this.$router.push({
        path: `/project/${project.id}`,
      });
    },
    projectCreationError() {},
    search(query) {
      this.query.term = query;
    },
    async saveUIPreferences(viewType) {
      try {
        this.selectedComponent = viewType;
        this.vModels.uiPreferences.projects.projectsViewMode = viewType;

        const profile = await this.$pigeonline.profile.save({
          projectsViewMode: this.vModels.uiPreferences.projects.projectsViewMode,
        });
        this.setProfile(profile);
      } catch (e) {
        alert('An error occured when processing your request. Please try again later');
        throw new Error('Projects.vue:saveUIPreferences ' + e.message);
      }
    },
    setValue(property, val) {
      if (val) {
        this.vModels.uiPreferences.projects[property] = val;
      }
    },
  },
};
</script>
